import { createPortal } from "react-dom";

// mui cm
import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";

// request
import { loginStudentRequest, loginTeacherRequest } from "api/requests";

// static image
import logo from "assets/img/Group 4 1.svg";

import LoadingCM from "components/shared/Loading";
import Toast from "helper/toast";

// hooks
import { yupResolver } from "@hookform/resolvers/yup";
import useAuth from "hooks/use-auth";
import useHttp from "hooks/use-http";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";

// mui config
import { MuiThemeProivider } from "styles/mui-config";
import RTL from "styles/rtl-config";

// validation
import { loginSchema } from "validations";

// styled cm
import {
  BackBtn,
  StyledContainer,
  StyledGrid,
  StyledImg,
  StyledTextField,
} from "./Login.style";

// icons
import { IoIosPerson } from "react-icons/io";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { HiOutlineArrowRight } from "react-icons/hi";
import { setUserData } from "store/user-storage";

export default function Login() {
  const { loading, setLoading, request } = useHttp();
  const Auth = useAuth();

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const [radioValue, setRadioValue] = useState("student");

  // password visibility icon state
  const [toggle, setToggle] = useState({ passwordVisibility: false });

  // handle visibility password
  const handleChangeVisibility = () => {
    setToggle({ passwordVisibility: !toggle.passwordVisibility });
  };

  const onSubmit = async (data) => {
    const transformedData =
      radioValue === "teacher"
        ? { national_code: data.username, password: data.pass }
        : { username: data.username, password: data.pass };
    const result = await request({
      url: radioValue === "teacher" ? loginTeacherRequest : loginStudentRequest,
      values: transformedData,
    });

    const res = radioValue === "teacher" ? result.data : result;

    if (res) {
      setUserData({
        type: res.type,
        fullName: `${res.user.first_name} ${res.user.last_name}`,
        isBlocked: res.user.is_block,
        blockReason: res.user.block_reason,
      });
      Auth.login(res.token);
      Toast({ type: "success", title: "ورود " });
      return navigate("/");
    }
  };

  useEffect(() => {
    if (Auth.isLoggedIn) return navigate("/");
    setLoading(false);
  }, []);
  return loading ? (
    <LoadingCM />
  ) : (
    createPortal(
      <>
        <MuiThemeProivider>
          <RTL>
            <div dir="rtl">
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{ background: "#fff", display: "flex" }}
                height="100vh"
                justifyContent={"center"}
              >
                <BackBtn>
                  <Stack px={{ md: 15, xs: 5 }}>
                    <IconButton onClick={() => navigate(-1)}>
                      <HiOutlineArrowRight />
                    </IconButton>
                  </Stack>
                </BackBtn>
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                  display={{ md: "none" }}
                >
                  <StyledImg src={logo} alt="" />
                </Stack>
                <StyledContainer container>
                  <Grid item xs={12} md={7} lg={6} p={{ xs: 5, md: 0 }}>
                    <Stack
                      px={{ md: 15 }}
                      height={"100%"}
                      justifyContent="center"
                      alignItems="center"
                      spacing={4}
                    >
                      <Typography
                        variant="h4"
                        component={"h2"}
                        fontWeight={"bold"}
                        color="primary"
                        fontFamily={"Aviny"}
                      >
                        ورود به حساب کاربری
                      </Typography>
                      <FormControl fullWidth>
                        <RadioGroup
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                          value={radioValue}
                          onChange={(e) => setRadioValue(e.target.value)}
                        >
                          <FormControlLabel
                            value="student"
                            control={<Radio />}
                            label="دانش آموز"
                          />
                          <FormControlLabel
                            value="teacher"
                            control={<Radio />}
                            label="مدرس"
                          />
                        </RadioGroup>
                      </FormControl>
                      <StyledTextField
                        label="نام کاربری"
                        variant="filled"
                        size="small"
                        fullWidth
                        id="username"
                        type={"text"}
                        {...register("username")}
                        error={Boolean(errors.username)}
                        helperText={errors.username?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IoIosPerson />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <StyledTextField
                        label="کلمه عبور"
                        variant="filled"
                        size="small"
                        fullWidth
                        id="pass"
                        {...register("pass")}
                        error={Boolean(errors.pass)}
                        helperText={errors.pass?.message}
                        type={toggle.passwordVisibility ? "text" : "password"}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleChangeVisibility}
                              >
                                {toggle.passwordVisibility && <Visibility />}
                                {!toggle.passwordVisibility && (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        onClick={handleSubmit(onSubmit)}
                        variant="contained"
                        fullWidth
                        sx={{
                          fontFamily: "Aviny",
                          fontSize: "1.5rem",
                          padding: 0,
                        }}
                      >
                        ورود
                      </Button>
                    </Stack>
                  </Grid>
                  <StyledGrid item />
                </StyledContainer>
              </Stack>
            </div>
          </RTL>
        </MuiThemeProivider>
      </>,
      document.getElementById("login")
    )
  );
}
